import ls, { get } from 'local-storage';

import { IIPENNotaryNewRegistrationObj } from '../../../types/Notary';
import { ISearchParams } from '../../type';
import axiosInterceptor from '../../utils/axios';
import { ConfigIProps } from '../orders/ordersTypes';
import { IIPENNotaryStatusUpdate, ISsoTokenRequest } from './ipenNotaryTypes';

export const confirmNotaryRequest = (orderId: string, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/confirm-notary';
    axiosInterceptor
      .put(url, { OrderId: orderId })
      .then(() => {
        resolve({ message: 'Notary has been confirmed successfully' });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getAllNotaries = (config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/notaries?Limit=10&Page=0';
    axiosInterceptor
      .get(url)
      .then((response) => {
        ls('ptn', response.data.PaginationToken);
        resolve(response.data);
      })
      .catch(() => {
        reject(
          'There was a problem fetching notaries. Please try again later.'
        );
      });
  });
};

export const getNotaryByEmail = (email: string, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/notaries?Email=' + email;
    axiosInterceptor
      .get(url)
      .then((response) => {
        resolve(response.data.Notaries[0]);
      })
      .catch(() => {
        reject(
          'There was a problem fetching this notary. Please try again later.'
        );
      });
  });
};

export const getSsoToken = (
  notaryRequest: ISsoTokenRequest,
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url =
      config.baseURL +
      `/order-management/get-sso-token?OrderId=${notaryRequest.OrderId}&TicketId=${notaryRequest.TicketId}`;
    axiosInterceptor
      .get(url)
      .then((response) => {
        resolve(response.data.Token);
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const getUserLoginDetails = (
  email: string | null,
  config: ConfigIProps
) => {
  return new Promise<{ HasLoggedIn: boolean }>((resolve, reject) => {
    const url = config.baseURL + `/admin/get-user-login-details?Email=${email}`;
    axiosInterceptor
      .get(url)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const resendRegistrationEmail = (
  notaryEmails: string[],
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/admin/resend-notary-registration-email';
    const submitObj = {
      Emails: notaryEmails,
    };
    axiosInterceptor
      .post(url, submitObj)
      .then(() => {
        resolve({
          message: `A registration link has been resent to ${submitObj.Emails[0]}.`,
        });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};
/**
 *
 * @param {Object} searchValues
 * @param {!string} searchValues.query
 * @param {string} searchValues.query.Branch
 * @param {string} searchValues.query.Status
 * @param {*} searchValues.query.DateToCompareCommissionExpirationTo
 * @param {boolean} searchValues.query.CommissionExpirationDateIsAfter
 * @param {number} searchValues.query.Limit
 * @param {number} searchValues.query.Page
 * @param {ConfigIProps} config
 * @returns
 */
export const searchNotaries = (
  searchValues: ISearchParams,
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const urlParams = new URLSearchParams(searchValues.query);
    const ptsLocal: string = get('ptn');
    let tokensArray = ptsLocal ? ptsLocal.split(',') : [];
    let paginationTokenAddition = '';
    let page: number = urlParams.get('Page')
      ? Number(urlParams.get('Page'))
      : 0;

    // add pagination token
    // skip is used only for notary dropdown loadMore as pagination is already managed
    switch (searchValues.direction) {
      case 'prev':
        paginationTokenAddition += `&PaginationToken=${tokensArray[page - 1]}`;
        tokensArray.length = page;
        break;
      case 'next':
        paginationTokenAddition += `&PaginationToken=${tokensArray[page - 1]}`;
        break;
      case 'start':
        tokensArray = [];
        page = 0;
        break;
      case 'skip':
        break;
      default:
        if (page > 0) {
          paginationTokenAddition += `&PaginationToken=${
            tokensArray[page - 1]
          }`;
        }
        break;
    }

    const url = config.baseURL.concat(
      '/notaries',
      searchValues.query,
      paginationTokenAddition
    );
    axiosInterceptor
      .get(url)
      .then((response) => {
        tokensArray[page] = response.data.PaginationToken;
        ls('ptn', tokensArray.join());
        resolve(response.data);
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const sendRegistrationEmail = (
  data: IIPENNotaryNewRegistrationObj,
  config: ConfigIProps
) => {
  return new Promise<Record<string, unknown>>((resolve, reject) => {
    const url = config.baseURL + '/admin/send-notary-registration-email';
    const submitObj = {
      Records: data.Notaries,
      OrderId: data.OrderId,
    };
    axiosInterceptor
      .post(url, submitObj)
      .then((response) => {
        if (response.data.ErrorList.length > 0) {
          resolve({
            message: response.data.ResponseMessage,
            messageType: 'error',
            dialog: true,
          });
        }
        resolve({
          message: response.data.ResponseMessage,
          messageType: 'success',
          dialog: false,
        });
      })
      .catch((error) => {
        reject(error?.response?.data);
      });
  });
};

export const submitNotary = (notary: any, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/notaries/register';
    if (!notary.Email.trim().toLowerCase().includes('@firstam.com')) {
      delete notary.BranchName;
      delete notary.BranchId;
    }
    axiosInterceptor
      .post(url, notary)
      .then(() => {
        resolve({
          notary: notary,
        });
      })
      .catch((error) => {
        reject(error.message);
      });
  });
};

export const updateNotary = (notary: any, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/notaries';
    if (!notary.Email.trim().toLowerCase().includes('@firstam.com')) {
      delete notary.BranchName;
      delete notary.BranchId;
    }
    axiosInterceptor
      .put(url, notary)
      .then(() => {
        resolve({
          notary: notary,
        });
      })
      .catch((error) => {
        reject(error.message);
      });
  });
};

export const updateNotaryStatus = (
  notaryInformation: IIPENNotaryStatusUpdate,
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/notaries/update-status';
    axiosInterceptor
      .put(url, notaryInformation)
      .then(() => {
        resolve({ message: 'Status has been updated successfully' });
      })
      .catch((error) => {
        reject(error.message);
      });
  });
};

export const getNotarySealTemplate = async (
  stateAbbreviation: string,
  config: ConfigIProps
) => {
  const url = `${
    config.baseURL
  }/notaries/get-seal-template?StateAbbreviation=${encodeURIComponent(
    stateAbbreviation
  )}`;

  return axiosInterceptor
    .get(url)
    .then((response) => {
      return response.status === 204 ? response.status : response.data;
    })
    .catch((error) => {
      error?.response?.data?.Message
        ? error.response.data.Message
        : 'There was a problem fetching the notary seal template. Please try again later.';
    });
};
